import React, { useState, useEffect } from 'react';
import AdminLayout from '../layouts/AdminLayout';
import adminApi from '../api/adminApi';
import { FaSearch, FaTrash, FaFilter } from 'react-icons/fa';

const STATUS_OPTIONS = ['Pending', 'Confirmed', 'Cancelled', 'Completed'];
const STATUS_COLORS = { Pending: 'pending', Confirmed: 'confirmed', Cancelled: 'cancelled', Completed: 'completed' };

const AdminAppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [deleteId, setDeleteId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState('');

  const fetchAppointments = async () => {
    try { const res = await adminApi.get('/appointments'); setAppointments(res.data); }
    catch { setError('Failed to load appointments.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAppointments(); }, []);

  const handleStatusChange = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await adminApi.put(`/appointments/${id}/status`, JSON.stringify(newStatus), {
        headers: { 'Content-Type': 'application/json' },
      });
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
    } catch { setError('Failed to update status.'); }
    finally { setUpdatingId(null); }
  };

  const handleDelete = async () => {
    try { await adminApi.delete(`/appointments/${deleteId}`); setDeleteId(null); await fetchAppointments(); }
    catch { setError('Failed to delete.'); setDeleteId(null); }
  };

  const filtered = appointments.filter(a => {
    const matchSearch = !search ||
      a.patientName?.toLowerCase().includes(search.toLowerCase()) ||
      a.email?.toLowerCase().includes(search.toLowerCase()) ||
      a.doctorName?.toLowerCase().includes(search.toLowerCase()) ||
      a.reason?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || a.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // Status counts
  const counts = appointments.reduce((acc, a) => { acc[a.status || 'Pending'] = (acc[a.status || 'Pending'] || 0) + 1; return acc; }, {});

  return (
    <AdminLayout title="Appointments" subtitle="View and manage all patient appointment bookings">
      {error && <div className="admin-login-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* Status Summary Chips */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }} id="appt-status-summary">
        {['All', ...STATUS_OPTIONS].map(s => (
          <button
            key={s}
            id={`appt-filter-${s.toLowerCase()}`}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: '0.45rem 1.1rem', borderRadius: 20,
              border: statusFilter === s ? '2px solid var(--admin-teal)' : '2px solid var(--admin-border)',
              background: statusFilter === s ? 'var(--admin-teal)' : 'white',
              color: statusFilter === s ? 'white' : 'var(--admin-muted)',
              fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {s} {s !== 'All' && counts[s] ? `(${counts[s]})` : s === 'All' ? `(${appointments.length})` : '(0)'}
          </button>
        ))}
      </div>

      <div className="admin-card" id="admin-appointments-card">
        <div className="admin-card-header">
          <div className="admin-card-title">
            {statusFilter === 'All' ? 'All Appointments' : `${statusFilter} Appointments`} — {filtered.length} found
          </div>
          <div className="admin-search">
            <FaSearch className="admin-search-icon" />
            <input id="appt-search" placeholder="Search patient, doctor, reason…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="admin-table-wrapper">
          <table className="admin-table" id="appointments-table">
            <thead>
              <tr>
                <th>#</th><th>Patient</th><th>Doctor</th><th>Date</th>
                <th>Reason</th><th>Notes</th><th>Status</th><th>Booked On</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading appointments…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9}><div className="admin-empty"><div className="admin-empty-icon">📅</div><div className="admin-empty-text">No appointments found.</div></div></td></tr>
              ) : filtered.map((a, i) => (
                <tr key={a.id} id={`appt-row-${a.id}`}>
                  <td style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 700 }}>{a.patientName}</div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{a.email}</div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{a.phone}</div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{a.doctorName}</div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{a.doctorSpecialty}</div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                    <div>{new Date(a.appointmentDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                  </td>
                  <td style={{ maxWidth: 150, fontSize: '0.82rem' }}>{a.reason}</td>
                  <td style={{ maxWidth: 140, fontSize: '0.78rem', color: '#64748b' }}>{a.notes || '—'}</td>
                  <td>
                    <select
                      className="status-select"
                      id={`appt-status-select-${a.id}`}
                      value={a.status || 'Pending'}
                      disabled={updatingId === a.id}
                      onChange={e => handleStatusChange(a.id, e.target.value)}
                      style={{ borderColor: a.status === 'Confirmed' ? '#10b981' : a.status === 'Cancelled' ? '#ef4444' : a.status === 'Completed' ? '#3b82f6' : '#f59e0b' }}
                    >
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {updatingId === a.id && <span style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block' }}>Saving…</span>}
                  </td>
                  <td style={{ fontSize: '0.78rem', whiteSpace: 'nowrap', color: '#94a3b8' }}>
                    {new Date(a.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </td>
                  <td>
                    <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => setDeleteId(a.id)} id={`delete-appt-${a.id}`}><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirm */}
      {deleteId && (
        <div className="admin-modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="admin-modal admin-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-body" style={{ textAlign: 'center', padding: '2.5rem 2rem' }}>
              <div className="admin-confirm-icon">🗑️</div>
              <div className="admin-confirm-title">Delete Appointment?</div>
              <p className="admin-confirm-text">This appointment record will be permanently removed.</p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-ghost" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="admin-btn admin-btn-danger" onClick={handleDelete} id="appt-delete-confirm">🗑️ Delete</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminAppointmentsPage;
